import React, { FC } from "react";
import Link from "next/link";
import { t } from "@helpers/translate";
import { PostCardFragment } from "@graphql/types";
import { getDateFormat } from "@helpers/getDateFormat";

import { Avatar } from "@components/ui/Avatar";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { Image } from "@components/ui/Image";
import { Card } from "@components/ui/Card";
import { twMerge } from "tailwind-merge";

type Props = {
  orientation: "vertical" | "horizontal";
  showUser?: boolean;
  post: PostCardFragment;
  cardStyle?: string;
};
const PostCard: FC<Props> = ({
  orientation,
  showUser = true,
  post,
  cardStyle,
}) => {
  if (orientation === "vertical") {
    return (
      <Link href={getAbsoluteUrl(`${post.url}`)} className="w-full">
        <div
          className={twMerge(
            "bg-white rounded-md shadow-lg w-full max-w-sm flex flex-col sm:mb-0",
            cardStyle
          )}
        >
          {/* Image with aspect ratio */}
          <div className="relative grow w-full">
            <div
              className="relative w-full bg-gray-200"
              style={{ paddingBottom: "75%" /* 4:3 aspect */ }}
            >
              <Image
                key={post.id}
                src={post?.picture?.url}
                alt={post.title || ""}
                className="w-full object-cover h-48 rounded-t-md"
                fill={true}
                activeCache={false}
              />
            </div>

            {/* Country badge wrapper */}
            {post.country?.name && (
              <div className="absolute top-2 left-2 w-full pr-2">
                <div
                  className="inline-block bg-gray-200 text-gray-600 rounded px-3 py-1 text-xs truncate"
                  style={{ maxWidth: "40%" }}
                >
                  {post.country?.name}
                </div>
              </div>
            )}
          </div>

          <div className="grow w-full px-3 pt-3">
            {/* Title  */}
            <div
              className="text-xl font-medium truncate-3 mb-2"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: "1.75rem", // matches Tailwind's leading-5
                height: "5.5rem", // 3 lines × 1.25rem
              }}
            >
              {post.title}
            </div>

            {/*  User details  */}
            {showUser && (
              <div
                className="align-middle border-t border-gray-200 py-2 flex items-center"
                style={{
                  minHeight: "60px",
                }}
              >
                <Avatar
                  src={post.user.picture && post.user.picture.url}
                  alt={post.user.displayName}
                  size="xs"
                />
                <div className="ml-2 w-full grid grid-cols-2 lg:grid lg:grid-cols-2 max-md:grid max-md:grid-cols-2">
                  <div className="float-left text-blue-900 text-sm font-medium truncate ">
                    {post.user.displayName}
                  </div>

                  {post.contentUpdatedAt && (
                    <div className="float-right text-gray-500 text-xs truncate text-right">
                      {getDateFormat(post.contentUpdatedAt, "MMM dd, yyyy")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  }

  if (orientation === "horizontal") {
    return (
      <Link
        href={getAbsoluteUrl(`${post.url}`)}
        title={t({
          id: "common.link.title",
          message: "GoWithGuide - Private Tours & Local Tour Guides",
        })}
      >
        <Card className="flex flex-row w-full">
          <div className="flex flex-col w-1/3">
            <div className="relative h-full w-full">
              <Image
                src={(post.picture && post.picture.url) || ""}
                alt={post.title || ""}
                className="w-full h-full object-cover"
                fill={true}
                activeCache={true}
              />
              {post.category && (
                <div className="absolute text-xs px-2 py-1 bottom-2 right-2 rounded-xl bg-gray-200  text-gray-700">
                  <div className="truncate">{post.category?.name}</div>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-2/3">
            <Card.Container className="flex flex-col w-full">
              <Card.Body>
                <Card.Title className="truncate w-full">
                  {post.title}
                </Card.Title>

                {/* Country, Area */}
                {post.country && (
                  <div className="flex max-w-md my-3 truncate">
                    <p className="truncate bg-gray-200 bg-gray-200/70 text-gray-600 rounded px-3 py-1 text-xs">
                      {`${
                        post.city?.name != null ? `${post.city?.name}, ` : ""
                      }`}

                      {`${
                        post.country?.name != null
                          ? `${post.country?.name}`
                          : ""
                      }`}
                    </p>
                  </div>
                )}

                <Card.Description className="truncate-3">
                  {post.overview}
                </Card.Description>
              </Card.Body>
              {showUser && (
                <div className="flex w-full flex-row items-center">
                  <Avatar
                    src={post.user.picture && post.user.picture.url}
                    alt={post.user.displayName}
                    size="xs"
                  />
                  <div className="ml-2 w-full grid grid-cols-2 lg:grid lg:grid-cols-2 max-md:grid max-md:grid-cols-2">
                    <div className="float-left text-blue-900 text-sm font-medium truncate ">
                      {post.user.displayName}
                    </div>

                    {post.contentUpdatedAt && (
                      <div className="ml-auto text-gray-500 text-xs truncate text-right">
                        {getDateFormat(post.contentUpdatedAt, "MMM dd, yyyy")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card.Container>
          </div>
        </Card>
      </Link>
    );
  }

  return null;
};

export default PostCard;
